import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import './Approvisionnement.css';
import Card15 from '../components/Card15';
import Card16 from '../components/Card16';
import Card18 from '../components/Card18';
import Card19 from '../components/Card19';
import Card20 from '../components/Card20';
import Card21 from '../components/Card21';
import Camembert3 from '../components/Camembert3';
import Graph2 from '../components/Graph2';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, DataLabelsPlugin);
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';
// ★ RÉGLAGES LARGEURS COLONNES — table20 (analyse_delai_approvisionnement)
// Modifie uniquement les valeurs px selon tes besoins
const COLUMN_WIDTHS_TABLE20 = {
  'Item':                                    '180px',
  'Description':                             '260px',
  'Délai appro actuel (jours ouv.)':         '120px',
  'Délais proposé':                          '85px',
  'Ecart':                                    '70px',
  'Evolution':                                '100px',
  'Qté liv 10 dernière commande':            '120px',
  'Délai moyen réel (jours ouv.)':           '75px',
   'Délais moyen':           '75px',
   'Délais mini':           '75px',
   'Délais max':           '75px',
      'Délais systèmes':           '90px',
        'Délais réel corrigé':           '90px',
    'C':           '55px',
        'C-1':           '55px',
            'C-2':           '55px',
                'C-3':           '55px',
                    'C-4':           '55px',
                        'C-5':           '55px',
                            'C-6':           '55px',
                                'C-7':           '55px',
                                    'C-8':           '55px',
                                        'C-9':           '55px',
};
// Toute colonne absente du mapping recevra cette largeur par défaut :
const TABLE20_DEFAULT_WIDTH = '110px';
// ★ RÉGLAGES — taille de la modal de détail article (fiche article)
// Modifie uniquement ces deux valeurs pour agrandir/réduire la modal
const MODAL_CONFIG = {
  maxWidth:   '1500px', // largeur max de la modal
  maxHeightVh: 92,      // hauteur max, en % de la hauteur de fenêtre
};
// ★ RÉGLAGES — design table2 (recalage commande frn — stock fort)
// Palette dérivée de l'identité MYSSA (navy / blue / cyan)
const T2_THEME = {
  navy:       '#0D2140',
  blue:       '#1A5FA8',
  cyan:       '#2BAADB',
  surface:    '#FFFFFF',
  canvas:     '#F6F9FC',
  border:     '#E3E9F0',
  textMain:   '#16243B',
  textSoft:   '#64748B',
  retard:     { fg: '#C0392B', bg: '#FCEDEB' },
  j:          { fg: '#B7770D', bg: '#FFF8E6' },
  ok:         { fg: '#1E8E5A', bg: '#EAF8F1' },
};
const getT2StatusMeta = (value) => {
  if (value === 'Retard') return T2_THEME.retard;
  if (value === 'J')      return T2_THEME.j;
  return T2_THEME.ok;
};
// ★ Style commun "pastille de statut" — réutilisé sur tous les tableaux
// pour garder un seul et même langage visuel (au lieu d'aplats de fond pleine cellule/ligne)
const t2PillStyle = (meta) => ({
  display: 'inline-block',
  padding: '2px 9px',
  borderRadius: '999px',
  fontSize: '11.5px',
  fontWeight: 600,
  color: meta.fg,
  background: meta.bg,
  whiteSpace: 'nowrap',
});
// ★ Style bouton "Réinitialiser" — sobre, cohérent avec le reste de la page (pas de dégradé)
const t2ResetBtnStyle = {
  background: '#FFFFFF',
  color: T2_THEME.navy,
  border: `1px solid ${T2_THEME.border}`,
  padding: '6px 14px',
  borderRadius: '7px',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
};
// ──────────────────────────────────────────────────────────────────────────
// ★ Graphe stock projeté + simulation recalage — repris de Stockprojete.jsx
// pour garder le même calcul entre les deux pages.
// ──────────────────────────────────────────────────────────────────────────
const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));
const parseLocalDate = (str) => {
  if (!str) return null;
  const parts = String(str).split('/');
  if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return new Date(str);
};
const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const getWeekKey = (d) => {
  const tmp = new Date(d); tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7; tmp.setDate(tmp.getDate() - day + 1);
  return `${tmp.getFullYear()}-S${String(Math.ceil(
    ((tmp - new Date(tmp.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '00')}`;
};
const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows.map(r => ({ ...r, _periodKey: r.date }));
  const groups = {};
  rows.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    const g = groups[key];
    if (!g.lastRow || parseLocalDate(r.date) >= parseLocalDate(g.lastRow.date)) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']); if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision') g.sumPrevision += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.entries(groups).filter(([, g]) => g.lastRow).map(([key, g]) => ({
    ...g.lastRow,
    'depart client': g.sumDepart,
    'livraison fournisseur': g.sumLivraison,
    _sumPrevision: g.sumPrevision,
    _sumCommandeFerme: g.sumCommandeFerme,
    _periodKey: key,
  })).sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
};
const formatGraphLabel = (dateStr, granularity) => {
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return dateStr || '';
  if (granularity === 'jour') return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') return `S${String(Math.ceil(
    ((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '0')} ${d.getFullYear()}`;
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};
const buildChartData = (rows, granularity = 'mois') => {
  const agg = aggregateRows(rows, granularity);
  return {
    labels: agg.map((r) => formatGraphLabel(r.date, granularity)),
    datasets: [
      {
        label: 'Stock projeté', type: 'line',
        data: agg.map((r) => Math.round(parseFloat(r['stock projete']) || 0)),
        borderColor: T2_THEME.navy, backgroundColor: 'rgba(13,33,64,0.15)',
        borderWidth: 2.5, fill: false, tension: 0.3, datalabels: { display: false },
      },
      {
        label: 'Stock de sécurité', type: 'line',
        data: new Array(agg.length).fill(Math.round(parseFloat(agg[0]?.['stock mini'] || 0))),
        borderColor: T2_THEME.retard.fg, backgroundColor: 'rgba(192,57,43,0.08)',
        borderWidth: 1.5, fill: false, borderDash: [6, 4], datalabels: { display: false },
      },
      {
        label: 'Prévision', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'prévision' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumPrevision || 0)),
        backgroundColor: 'rgba(26,95,168,0.45)', borderColor: T2_THEME.blue,
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.blue, anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Commande ferme', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumCommandeFerme || 0)),
        backgroundColor: 'rgba(192,57,43,0.45)', borderColor: T2_THEME.retard.fg,
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.retard.fg, anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Livraison fournisseur', type: 'bar',
        data: agg.map((r) => Math.round(parseFloat(r['livraison fournisseur']) || 0)),
        backgroundColor: 'rgba(183,119,13,0.5)', borderColor: T2_THEME.j.fg,
        borderWidth: 1, stack: 'in', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: T2_THEME.j.fg, anchor: 'end', align: 'top', font: { size: 10 } },
      },
    ],
  };
};
const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  scales: {
    x: { stacked: false, ticks: { maxRotation: 45, minRotation: 0, font: { size: 11 } }, grid: { color: '#F3F4F6' } },
    y: { stacked: false, ticks: { font: { size: 11 } }, grid: { color: '#F3F4F6' } },
  },
  plugins: {
    legend: { display: true, labels: { font: { size: 12 }, boxWidth: 12 } },
    datalabels: { color: '#374151', font: { weight: 'bold', size: 10 } },
  },
};
// Applique reports + annulations de recalage_cmde_frn sur les lignes brutes et
// recalcule le stock projeté chronologiquement — fonction au niveau module,
// identique à Stockprojete.jsx, pour la courbe verte en pointillés du graphe.
const buildSimulatedRows = (rows, cmds) => {
  let simRows = rows.map(r => ({ ...r }));
  cmds.forEach(cmd => {
    const origDate   = cmd['Date de livraison'];
    const reportDate = cmd['Report proposé'];
    const statut     = (cmd.statut || '').trim().toUpperCase();
    const qty        = parseFloat(cmd['Quantité commandée']) || 0;
    if (statut === 'ANNULATION') {
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        const oDate = parseLocalDate(origDate) || new Date(origDate);
        if (!rDate || !oDate || isNaN(rDate) || isNaN(oDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
    } else if (reportDate && reportDate !== 'ANNULATION') {
      const oDate = parseLocalDate(origDate) || new Date(origDate);
      const nDate = parseLocalDate(reportDate) || new Date(reportDate);
      if (!oDate || !nDate || isNaN(oDate) || isNaN(nDate)) return;
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        if (!rDate || isNaN(rDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
      const existingIdx = simRows.findIndex(r => {
        const rDate = parseLocalDate(r.date);
        return rDate && !isNaN(rDate) && rDate.toDateString() === nDate.toDateString();
      });
      if (existingIdx >= 0) {
        simRows[existingIdx] = {
          ...simRows[existingIdx],
          'livraison fournisseur': (parseFloat(simRows[existingIdx]['livraison fournisseur']) || 0) + qty,
        };
      } else {
        const refRow = simRows[0] || {};
        const dd = nDate.getDate(), mm = nDate.getMonth() + 1, yyyy = nDate.getFullYear();
        simRows.push({
          ...refRow,
          date: `${String(dd).padStart(2,'0')}/${String(mm).padStart(2,'0')}/${yyyy}`,
          'type de donnee': 'commande frn',
          'livraison fournisseur': qty,
          'depart client': 0,
        });
      }
    }
  });
  simRows.sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
  let stockCourant = parseFloat(simRows[0]?.stock) || 0;
  simRows = simRows.map((r) => {
    const lf = parseFloat(r['livraison fournisseur']) || 0;
    const dc = parseFloat(r['depart client']) || 0;
    stockCourant = stockCourant + lf - dc;
    return { ...r, 'stock projete': stockCourant };
  });
  return simRows;
};
// Libellé Avancement / Report / Annulation / Inchangé — couleurs IDENTIQUES à
// celles utilisées dans la modale de Stockprojete.jsx (même format demandé).
const getStockprojeteStatutLabel = (c) => {
  const statut = (c.statut || '').trim().toUpperCase();
  if (statut === 'ANNULATION') return { label: 'Annulation', bg: '#FEE2E2', color: '#B91C1C' };
  const origD   = parseLocalDate(c['Date de livraison']) || new Date(c['Date de livraison']);
  const reportD = parseLocalDate(c['Report proposé'])    || new Date(c['Report proposé']);
  if (!origD || !reportD || isNaN(origD) || isNaN(reportD)) {
    return { label: c.statut || '—', bg: '#E8F4FD', color: '#1565C0' };
  }
  const diffJours = Math.round((reportD - origD) / (1000 * 60 * 60 * 24));
  if (diffJours < 0) return { label: `Avancement (${Math.abs(diffJours)}j)`, bg: '#EAF3DE', color: '#3B6D11' };
  if (diffJours > 0) return { label: `Report (${diffJours}j)`, bg: '#FFF3CD', color: '#856404' };
  return { label: 'Inchangé', bg: '#F3F4F6', color: '#6B7280' };
};
// ──────────────────────────────────────────────────────────────────────────
// ★ PROCHAINE LIVRAISON POSSIBLE — colonne ajoutée dans « Historique & projection »
//   Date = « Date de livraison » de la base article (aujourd'hui + délai appro).
//   Si elle est vide : aujourd'hui + délai d'appro (jours ouvrés).
//   La colonne n'a aucun mouvement : on y saisit une quantité dans « Simu cmde Frn »
//   pour simuler l'arrivée d'une commande passée aujourd'hui.
// ──────────────────────────────────────────────────────────────────────────
const TYPE_PROCHAINE_LIVRAISON = 'prochaine livraison';
const STYLE_COL_PROCHAINE = { background: '#E6F5FB', boxShadow: 'inset 2px 0 0 #2BAADB, inset -2px 0 0 #2BAADB' };
const pad2 = (n) => String(n).padStart(2, '0');
const dateFR = (d) => (d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}` : '');
// jj/mm/aaaa · AAAA-MM-JJ · ISO PostgreSQL envoyé en UTC ("2026-05-08T22:00:00.000Z" = 09/05 à Paris)
// → date locale à minuit, sans décalage d'un jour
const lireDateLocale = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const s = String(v).trim();
  let m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return null;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(s) && !(m[4] === '00' && m[5] === '00')) {
    const d = new Date(s);
    return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return new Date(+m[1], +m[2] - 1, +m[3]);
};
const ajouterJoursOuvres = (date, n) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  let reste = Math.max(0, Math.round(Number(n) || 0));
  while (reste > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) reste--;
  }
  return d;
};
// Insère la colonne « prochaine livraison » à sa place chronologique.
// Si une date identique existe déjà, cette colonne est simplement marquée (pas de doublon).
const insererProchaineLivraison = (rows, dateLivraison) => {
  if (!rows || rows.length === 0 || !dateLivraison) return rows;
  const cible = lireDateLocale(dateLivraison);
  if (!cible) return rows;
  const dateLigne = (r) => lireDateLocale(r.date) || parseLocalDate(r.date);
  const memeJour = rows.findIndex(r => { const d = dateLigne(r); return d && !isNaN(d) && d.toDateString() === cible.toDateString(); });
  if (memeJour >= 0) return rows.map((r, i) => (i === memeJour ? { ...r, _prochaineLivraison: true } : r));
  let idx = rows.findIndex(r => { const d = dateLigne(r); return d && !isNaN(d) && d > cible; });
  if (idx < 0) idx = rows.length;
  const num = (v) => parseFloat(v) || 0;
  // Stock à cette date = stock projeté de la colonne précédente (aucun mouvement ce jour-là)
  const stockAvant = idx > 0
    ? num(rows[idx - 1]['stock projete'])
    : num(rows[0]['stock projete']) - num(rows[0]['livraison fournisseur']) + num(rows[0]['depart client']);
  const ligne = {
    ...rows[Math.max(0, idx - 1)],
    date: dateFR(cible),
    'type de donnee': TYPE_PROCHAINE_LIVRAISON,
    'livraison fournisseur': 0,
    'depart client': 0,
    'stock projete': stockAvant,
    _prochaineLivraison: true,
  };
  return [...rows.slice(0, idx), ligne, ...rows.slice(idx)];
};
// ──────────────────────────────────────────────────────────────────────────
// ★ DATES DE REPORT PROPOSÉES — une colonne par date de « Report proposé »
//   (commandes recalées de l'article, hors annulations) dans « Historique & projection ».
//   Même principe que « Prochaine livraison » : aucune quantité, on saisit dans
//   « Simu cmde Frn » la quantité qu'on veut recevoir à cette date, puis export Excel.
// ──────────────────────────────────────────────────────────────────────────
const TYPE_DATE_REPORT = 'report proposé';
const STYLE_COL_REPORT = { background: '#FFF6E0', boxShadow: 'inset 2px 0 0 #E0A526, inset -2px 0 0 #E0A526' };
const ANNEE_ANNULATION = 2050; // « 31/12/2050 » = annulation dans recalage_cmde_frn
// Style de colonne mise en avant (prochaine livraison prioritaire sur date de report)
const styleColonneMarquee = (row) =>
  row._prochaineLivraison ? STYLE_COL_PROCHAINE
  : row._dateReport ? STYLE_COL_REPORT
  : row._ajoutUtilisateur ? STYLE_COL_AJOUT
  : undefined;
// ──────────────────────────────────────────────────────────────────────────
// ★ DATES AJOUTÉES PAR L'UTILISATEUR — bouton « + » entre deux colonnes de
//   « Historique & projection » : nouvelle colonne datée JOURS_AVANT_AJOUT jours avant
//   la colonne suivante (bornée entre la colonne précédente et la suivante).
//   Aucun mouvement : on y saisit une quantité dans « Simu cmde Frn ».
// ──────────────────────────────────────────────────────────────────────────
const JOURS_AVANT_AJOUT = 5;
const TYPE_DATE_AJOUTEE = 'date ajoutée';
const STYLE_COL_AJOUT = { background: '#F3EEFF', boxShadow: 'inset 2px 0 0 #7C3AED, inset -2px 0 0 #7C3AED' };
const UN_JOUR = 86400000;
const dateISO = (d) => (d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '');
const dateDeLigne = (r) => (r ? lireDateLocale(r.date) || parseLocalDate(r.date) : null);
// Date proposée pour une colonne insérée AVANT l'index idx, ou null si aucune place
const dateAjoutPossible = (rows, idx) => {
  const suivante = dateDeLigne(rows[idx]);
  if (!suivante || isNaN(suivante)) return null;
  const precedente = idx > 0 ? dateDeLigne(rows[idx - 1]) : null;
  let d = new Date(suivante.getFullYear(), suivante.getMonth(), suivante.getDate() - JOURS_AVANT_AJOUT);
  if (precedente && !isNaN(precedente) && d <= precedente) {
    d = new Date(precedente.getFullYear(), precedente.getMonth(), precedente.getDate() + 1);
  }
  return d < suivante ? d : null;
};
// Insère la colonne ajoutée avant l'index idx — stock = stock juste avant cette date
const insererDateAjoutee = (rows, idx, date) => {
  const num = (v) => parseFloat(v) || 0;
  const stockAvant = idx > 0
    ? num(rows[idx - 1]['stock projete'])
    : num(rows[0]['stock projete']) - num(rows[0]['livraison fournisseur']) + num(rows[0]['depart client']);
  const ligne = {
    ...rows[Math.max(0, idx - 1)],
    date: dateFR(date),
    'type de donnee': TYPE_DATE_AJOUTEE,
    'livraison fournisseur': 0,
    'depart client': 0,
    'stock projete': stockAvant,
    _prochaineLivraison: false,
    _dateReport: false,
    _ajoutUtilisateur: true,
  };
  return [...rows.slice(0, idx), ligne, ...rows.slice(idx)];
};
// Décale les saisies « Simu cmde Frn » (indexées par colonne) après insertion / suppression
const decalerSimu = (simu, aPartirDe, delta) => {
  const res = {};
  Object.entries(simu).forEach(([k, v]) => {
    const i = Number(k);
    if (delta < 0 && i === aPartirDe) return; // colonne supprimée
    res[i >= aPartirDe ? i + delta : i] = v;
  });
  return res;
};
// Dates de report distinctes, triées, hors annulations
const datesReportProposees = (cmds) => {
  const vues = new Set();
  const dates = [];
  (cmds || []).forEach(c => {
    if ((c.statut || '').trim().toUpperCase() === 'ANNULATION') return;
    const d = lireDateLocale(c['Report proposé']);
    if (!d || d.getFullYear() >= ANNEE_ANNULATION) return;
    if (vues.has(d.toDateString())) return;
    vues.add(d.toDateString());
    dates.push(d);
  });
  return dates.sort((a, b) => a - b);
};
// Insère une colonne « date de report » à sa place chronologique (ou marque la date existante)
const insererDateReport = (rows, dateReport) => {
  if (!rows || rows.length === 0 || !dateReport) return rows;
  const cible = lireDateLocale(dateReport);
  if (!cible) return rows;
  const dateLigne = (r) => lireDateLocale(r.date) || parseLocalDate(r.date);
  const memeJour = rows.findIndex(r => { const d = dateLigne(r); return d && !isNaN(d) && d.toDateString() === cible.toDateString(); });
  if (memeJour >= 0) return rows.map((r, i) => (i === memeJour ? { ...r, _dateReport: true } : r));
  let idx = rows.findIndex(r => { const d = dateLigne(r); return d && !isNaN(d) && d > cible; });
  if (idx < 0) idx = rows.length;
  const num = (v) => parseFloat(v) || 0;
  const stockAvant = idx > 0
    ? num(rows[idx - 1]['stock projete'])
    : num(rows[0]['stock projete']) - num(rows[0]['livraison fournisseur']) + num(rows[0]['depart client']);
  const ligne = {
    ...rows[Math.max(0, idx - 1)],
    date: dateFR(cible),
    'type de donnee': TYPE_DATE_REPORT,
    'livraison fournisseur': 0,
    'depart client': 0,
    'stock projete': stockAvant,
    _prochaineLivraison: false,
    _dateReport: true,
  };
  return [...rows.slice(0, idx), ligne, ...rows.slice(idx)];
};
// ──────────────────────────────────────────────────────────────────────────
// ★ PAGE — cartes du haut et titres des tableaux
// ──────────────────────────────────────────────────────────────────────────
const CARTES_AP = [
  { table: 'table1',  source: 'commande_a_10_jours_frn_api',          titre: 'Commandes fournisseurs à 10 jours',   Component: Card15 },
  { table: 'table2',  source: 'commande_frn_recalage_stock_fort_api', titre: 'Recalage — stock fort',               Component: Card16 },
  { table: 'table3',  source: 'recalage_cmde_frn_api',                titre: 'Recalage des commandes fournisseurs', Component: Card18 },
  { table: 'table4',  source: 'commandes_frn_sans_ar_api',            titre: 'Commandes fournisseurs sans AR',      Component: Card19 },
  { table: 'table20', source: 'analyse_delai_approvisionnement_pg',   titre: "Analyse des délais d'appro",          Component: Card20 },
  { table: 'table21', source: 'taches_pg',                            titre: 'Tâches APPRO',                        Component: Card21 },
];

// ★ FILTRES — une colonne n'a PAS de champ de filtre si son nom contient un de ces mots
//   (sans accents ni majuscules). Quantités, lignes, délais, écarts… restent sans filtre.
const MOTS_SANS_FILTRE = [
  'ligne', 'quantite', 'qte', 'stock', 'rupture', 'montant', 'prix',
  'ecart', 'evolution', 'delai', 'delais', 'age', 'commentaire',
];

// ★ FILTRES — colonnes supplémentaires sans filtre, tableau par tableau ('*' = aucun filtre)
const SANS_FILTRE_AP = {
  table1:  [],
  table2:  [],
  table3:  [],
  table4:  [],
  table20: ['c', 'c-1', 'c-2', 'c-3', 'c-4', 'c-5', 'c-6', 'c-7', 'c-8', 'c-9'],
  table21: '*',
};

const normaliser = (t) => String(t || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ');

// Libellés corrigés à l'affichage (les données ne changent pas)
const LIBELLES_AP = {
  'Numero de commande': 'Numéro de commande',
  'Numero de ligne': 'N° de ligne',
  'Description': 'Description',
  'Date de livraison prévue': 'Livraison prévue',
  'Qté liv 10 dernière commande': 'Qté 10 dernières livraisons',
};

// ★ Tâches — libellés reconnus dans la description (découpée en champs)
const CLES_DESCRIPTION = [
  'Date de livraison prevue', 'Date de livraison prévue', 'Item', 'Commande client', 'Ligne',
  'Commande fournisseur', 'Nom fournisseur', 'Date de réception', 'Date de reception', 'Commentaire',
];

const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REGEX_CLES = new RegExp(
  `(${[...CLES_DESCRIPTION].sort((a, b) => b.length - a.length).map(echapper).join('|')})\\s*:`,
  'g'
);

const decouperDescription = (texte) => {
  if (texte == null) return null;
  const s = String(texte);
  const positions = [];
  let m;
  REGEX_CLES.lastIndex = 0;
  while ((m = REGEX_CLES.exec(s)) !== null) {
    positions.push({ label: m[1], debut: m.index, finLabel: REGEX_CLES.lastIndex });
  }
  if (positions.length < 2) return null;
  const champs = [];
  const avant = s.slice(0, positions[0].debut).trim();
  if (avant) champs.push({ label: 'Info', valeur: avant });
  positions.forEach((p, i) => {
    const fin = i + 1 < positions.length ? positions[i + 1].debut : s.length;
    const valeur = s.slice(p.finLabel, fin).trim();
    champs.push({ label: p.label.replace('prevue', 'prévue').replace('reception', 'réception'), valeur: valeur || '—' });
  });
  return champs;
};

// Zone de commentaire qui s'agrandit pour afficher tout le texte
const AutoTextarea = ({ value, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} value={value} rows={2} {...props} />;
};

const Pill = ({ tone, children }) => <span className={`ap-badge ap-badge-${tone}`}>{children}</span>;

const toneHorizon = (v) => (v === 'Retard' ? 'danger' : v === 'J' ? 'warn' : 'ok');

const estNombre = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(String(v).replace(',', '.')));

// ★ RECALAGE CMDE FRN (table3) — une seule ligne par Item, quels que soient fournisseur / appro.
//   Quantités : additionnées. Autres colonnes : valeur unique, sinon valeurs distinctes « A / B ».
//   Colonne « Nb lignes » = nombre de lignes de commande regroupées.
// ★ Séparateur des valeurs regroupées : une valeur par ligne dans la cellule
const SEP_REGROUPE = '\n';
const estColonneQte  = (col) => /quantit|qte|qté/i.test(col);
const estColonneDate = (col) => /date|report/i.test(col);
const regrouperParItem = (rows, formaterDate) => {
  const groupes = new Map();
  rows.forEach(r => {
    const cle = (r.Item ?? '').toString().trim().toUpperCase();
    if (!groupes.has(cle)) groupes.set(cle, []);
    groupes.get(cle).push(r);
  });
  return [...groupes.values()].map(lignes => {
    if (lignes.length === 1) return { ...lignes[0], 'Nb lignes': 1, _lignes: lignes };
    const colonnes = [...new Set(lignes.flatMap(l => Object.keys(l)))];
    const res = {};
    colonnes.forEach(col => {
      const vals = lignes.map(l => l[col]);
      if (estColonneQte(col) && vals.every(v => v == null || v === '' || estNombre(v))) {
        res[col] = vals.reduce((s, v) => s + (parseFloat(String(v ?? 0).replace(',', '.')) || 0), 0);
        return;
      }
      const distincts = [];
      vals.forEach(v => {
        if (v == null || v === '') return;
        const t = estColonneDate(col) && String(v).trim().toUpperCase() !== 'ANNULATION' ? formaterDate(v) : String(v);
        if (!distincts.includes(t)) distincts.push(t);
      });
      res[col] = distincts.length <= 1 ? (distincts[0] ?? '') : distincts.join(SEP_REGROUPE);
    });
    res['Nb lignes'] = lignes.length;
    res._lignes = lignes;
    return res;
  });
};
const Approvisionnement = () => {
  
  const [data, setData] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [showCharts, setShowCharts] = useState(true);
  const [histogramData, setHistogramData] = useState([]);
  const [simuCmdeFrnValues, setSimuCmdeFrnValues] = useState({});
  const [projectedStockValues, setProjectedStockValues] = useState([]);
  const [stockMiniValue, setStockMiniValue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [recalageCmds, setRecalageCmds] = useState([]);
  const [itemClickError, setItemClickError] = useState(null);
  const [granularity, setGranularity] = useState('mois');
  const [filtres, setFiltres] = useState({});   // filtres par colonne { colonne: texte }
  const [exportSimu, setExportSimu] = useState(false);   // export Excel ajouts / reports en cours
  const [modeModale, setModeModale] = useState('appro'); // 'appro' (appro à lancer) | 'recalage' (commandes à recaler)
  const [cmdsExclues, setCmdsExclues] = useState(() => new Set()); // index des commandes décochées par l'opérateur
  
    useEffect(() => {                                    // ← ajouter ici
    document.title = 'MYSSA – Approvisionnement';
  }, []);
  // ================= FORMAT DATE jj/mm/aaaa =================
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(dateValue))) return dateValue;
    const dateObj = new Date(dateValue);
    if (isNaN(dateObj)) return dateValue;
    const day   = String(dateObj.getUTCDate()).padStart(2, '0');
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const year  = dateObj.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };
  // ================= TABLE 21 (TÂCHES) =================
  const TABLE21_COLUMNS = [
    "Date", "De", "Description", "Commentaires", "Statut", "Numero tache",
  ];
  const getTable21ColWidth = (col) =>
    col === "Date"          ? "50px"  :
    col === "De"            ? "50px"  :
    col === "Description"   ? "200px" :
    col === "Statut"        ? "80px"  :
    col === "Numero tache"  ? "40px"  :
    col === "Commentaires"  ? "300px" :
    "150px";
  const renderCommentaire = (text) => {
    if (!text || !text.includes("Commentaire :")) return text;
    const [before, after] = text.split("Commentaire :");
    return (
      <>
        <span>{before}</span>
        <span style={{ color: "red", fontWeight: "bold" }}>
          Commentaire : {after}
        </span>
      </>
    );
  };
  // ================= RECALCUL STOCK PROJETÉ =================
  // ★ La quantité saisie dans « Simu cmde Frn » est reçue à la date de SA colonne
  //   (ex. colonne « Prochaine livraison » → le stock augmente dès cette date, puis sur toutes les suivantes)
  const recalculateProjectedStock = (updatedSimuValues, data) => {
    const newProjected = [];
    for (let i = 0; i < data.length; i++) {
      const simu = parseFloat(updatedSimuValues[i]) || 0;
      if (i === 0) {
        newProjected.push((parseFloat(data[0]['stock projete']) || 0) + simu);
        continue;
      }
      const livFrn       = parseFloat(data[i]['livraison fournisseur']) || 0;
      const departClient = parseFloat(data[i]['depart client']) || 0;
      newProjected.push(newProjected[i - 1] + simu + livFrn - departClient);
    }
    setProjectedStockValues(newProjected);
  };
  // ================= COLONNES DE DATE AJOUTÉES PAR L'UTILISATEUR =================
  // Après chaque ajout / suppression : saisies décalées + stock projeté recalculé
  const appliquerNouvellesColonnes = (rows, simu) => {
    setHistogramData(rows);
    setSimuCmdeFrnValues(simu);
    if (projectedStockValues.length > 0 || Object.keys(simu).length > 0) {
      recalculateProjectedStock(simu, rows);
    }
  };
  const ajouterColonneDate = (idx) => {
    const d = dateAjoutPossible(histogramData, idx);
    if (!d) return;
    appliquerNouvellesColonnes(
      insererDateAjoutee(histogramData, idx, d),
      decalerSimu(simuCmdeFrnValues, idx, 1)
    );
  };
  const supprimerColonneDate = (idx) => {
    if (!histogramData[idx]?._ajoutUtilisateur) return;
    appliquerNouvellesColonnes(
      histogramData.filter((_, i) => i !== idx),
      decalerSimu(simuCmdeFrnValues, idx, -1)
    );
  };
  // Date d'une colonne ajoutée modifiable, en restant entre la colonne précédente et la suivante
  const changerDateAjoutee = (idx, valeurISO) => {
    const d = lireDateLocale(valeurISO);
    if (!d) return;
    const prec = idx > 0 ? dateDeLigne(histogramData[idx - 1]) : null;
    const suiv = idx < histogramData.length - 1 ? dateDeLigne(histogramData[idx + 1]) : null;
    if ((prec && d <= prec) || (suiv && d >= suiv)) return;
    setHistogramData(prev => prev.map((r, i) => (i === idx ? { ...r, date: dateFR(d) } : r)));
  };
  // ================= CHARGEMENT DES DONNÉES =================
  const loadTableData = async (source, tableName) => {
    try {
      let jsonData = [];
      if (source === 'analyse_delai_approvisionnement_pg') {
        const response = await fetch(`${API}/analyse_delai_approvisionnement`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, Id, Client_id, ...rest }) => rest);
      }
      else if (source === 'commande_a_10_jours_frn_api') {
        const response = await fetch(`${API}/commande_a_10_jours_frn`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, client_id, Client_id, ...rest }) => rest);
      }
      else if (source === 'recalage_cmde_frn_api') {
        const response = await fetch(`${API}/recalage_cmde_frn`);
        const rawData = await response.json();
        let cleanedData = rawData.map(({ Id, Client_id, ...rest }) => rest);
        cleanedData = cleanedData.map(row => {
          if (row["Report proposé"] === "31/12/2050") {
            return { ...row, "Report proposé": "ANNULATION" };
          }
          return row;
        });
        cleanedData.sort((a, b) => {
          if (!a.Item) return 1;
          if (!b.Item) return -1;
          return a.Item.localeCompare(b.Item, 'fr', { sensitivity: 'base' });
        });
        jsonData = cleanedData;
      }
      else if (source === 'commande_frn_recalage_stock_fort_api') {
        const response = await fetch(`${API}/commande_frn_recalage_stock_fort`);
        const rawData = await response.json();
        jsonData = rawData.map(({ Id, ID, Client_id, client_id, ...rest }) => rest);
      }
      else if (source === 'commandes_frn_sans_ar_api') {
        const response = await fetch(`${API}/commandes_frn_sans_ar`);
        const rawData = await response.json();
        jsonData = rawData.map(({ ID, Client_id, Age, client_id, ...rest }) => {
          if (rest['Date de commande']) {
            const dateObj = new Date(rest['Date de commande']);
            if (!isNaN(dateObj)) {
              const day   = String(dateObj.getDate()).padStart(2, '0');
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const year  = dateObj.getFullYear();
              rest['Date de commande'] = `${day}/${month}/${year}`;
            }
          }
          return rest;
        });
      }
      else if (source === 'taches_pg') {
        const response = await fetch(`${API}/taches`);
        const rawData = await response.json();
        let cleanedData = rawData.map(
          ({ Id, id, client_id, Client_id, created_at, updated_at, ...rest }) => rest
        );
  jsonData = cleanedData
  .filter(row =>
    row.Département === "APPRO" &&
    (row.Statut === "ouvert" || row.Statut === "en cours")
  )
  .sort((a, b) => new Date(a['Date'] || 0) - new Date(b['Date'] || 0));
      }
      else {
        const response = await fetch(`/${source}`);
        jsonData = await response.json();
      }
      setData(jsonData);
      setActiveTable(tableName);
      setFilteredData([]);
      setHistogramData([]);
      setSimuCmdeFrnValues({});
      setProjectedStockValues([]);
      setRecalageCmds([]);
      setGranularity('mois');
      setShowDetailModal(false);
      setShowCharts(false);
      setItemClickError(null);
    } catch (error) {
      console.error('Erreur chargement données', error);
    }
  };
  // ================= FERMETURE MODAL DÉTAIL ITEM =================
  const closeDetailModal = () => {
    setFilteredData([]);
    setHistogramData([]);
    setSimuCmdeFrnValues({});
    setProjectedStockValues([]);
    setStockMiniValue(null);
    setRecalageCmds([]);
    setCmdsExclues(new Set());
    setGranularity('mois');
    setShowDetailModal(false);
    setItemClickError(null);
  };
  // ================= CLIC SUR ITEM (Card16 / Card18) =================
  // Normalise une référence Item pour comparer deux tables qui peuvent avoir des
  // écarts d'espaces ou de casse (ex. " ABC123 " vs "abc123") sans être identiques.
  const normalizeItem = (v) => (v ?? '').toString().trim().toUpperCase();
  // ★ Deux modales distinctes selon le tableau d'origine :
  //   mode 'appro'    (table2) → appro à lancer : colonne « Prochaine livraison »
  //   mode 'recalage' (table3) → commandes à recaler : une colonne par date de report proposée
  //   En mode 'recalage', le tableau affiché derrière ne change pas.
  const handleItemClick = async (value, mode = 'appro') => {
    const cleanValue = normalizeItem(value);
    setItemClickError(null);
    // La modal s'ouvre tout de suite avec ce qu'on a déjà (le code Item) — elle ne
    // doit jamais rester fermée parce qu'une seule des trois sources a un souci.
    // Chaque section se complète ensuite indépendamment au fur et à mesure.
    if (mode === 'appro') setActiveTable('table2');
    setModeModale(mode);
    setShowDetailModal(true);
    setHistogramData([]);
    setFilteredData([{ Item: value }]);
    setRecalageCmds([]);
    setCmdsExclues(new Set());
    setStockMiniValue(null);
    setSimuCmdeFrnValues({});
    setProjectedStockValues([]);

    let filteredHistogram = [];
    try {
      const responseHistogram = await fetch(`${API}/donnee_histogramme`);
      const rawDataHistogram = await responseHistogram.json();
      const cleanDataHistogram = rawDataHistogram.map(
        ({ Id, Client_id, client_id, ...rest }) => rest
      );
      filteredHistogram = cleanDataHistogram
        .filter(row => normalizeItem(row.Item) === cleanValue)
        .sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
      setHistogramData(filteredHistogram);
    } catch (err) {
      console.error('Erreur chargement donnee_histogramme', err);
    }

    const firstNegativeRow = filteredHistogram.find(
      row => parseFloat(row['stock projete']) < 0
    );
    const dateRuptureCalculee = firstNegativeRow
      ? formatDate(firstNegativeRow.date)
      : "";

    try {
      const responseItem = await fetch(`${API}/base_de_donne_item`);
      const rawDataItem = await responseItem.json();
      const cleanDataItem = rawDataItem.map(
        ({ ID, Client_id, client_id, ...rest }) => rest
      );
      // ★ Prochaine date de livraison possible : base article, sinon aujourd'hui + délai appro (jours ouvrés)
      const itemBrut = cleanDataItem.find(row => normalizeItem(row.Item) === cleanValue);
      let dateProchaine = itemBrut ? lireDateLocale(itemBrut["Date de livraison"]) : null;
      if (!dateProchaine && itemBrut) {
        const delai = parseFloat(itemBrut["Délai d'approvisionnement (jours ouvrés)"]);
        if (!isNaN(delai)) dateProchaine = ajouterJoursOuvres(new Date(), delai);
      }
      const filteredItem = cleanDataItem
        .filter(row => normalizeItem(row.Item) === cleanValue)
        .map(row => ({
          ...row,
          "Date de livraison": dateProchaine ? dateFR(dateProchaine) : formatDate(row["Date de livraison"]),
          "Date de rupture": dateRuptureCalculee,
        }));
      if (mode === 'appro' && dateProchaine && filteredHistogram.length > 0) {
        setHistogramData(insererProchaineLivraison(filteredHistogram, dateProchaine));
      }
      if (filteredItem.length > 0) {
        setFilteredData(filteredItem);
        setStockMiniValue(
          filteredItem[0]['Stock mini'] !== undefined ? parseFloat(filteredItem[0]['Stock mini']) : null
        );
      } else {
        // Item absent de base_de_donne_item : la fiche reste affichée (avec le code
        // Item et le reste des données disponibles), simplement signalée.
        setItemClickError(
          `Item "${value}" introuvable dans la base article (base_de_donne_item) — fiche affichée avec les seules données disponibles.`
        );
      }
    } catch (err) {
      console.error('Erreur chargement base_de_donne_item PG', err);
      setItemClickError(`Erreur lors du chargement de la fiche article pour "${value}".`);
    }

    // Commandes fournisseur déjà recalées pour cet item (toutes, y compris ANNULATION,
    // pour alimenter à la fois le tableau de recalage et la courbe simulée du graphe)
    try {
      const responseRecalage = await fetch(`${API}/recalage_cmde_frn`);
      const rawRecalage = await responseRecalage.json();
      const cleanRecalage = rawRecalage.map(({ Id, Client_id, ...rest }) => rest);
      const cmdsItem = cleanRecalage.filter(row => normalizeItem(row.Item) === cleanValue);
      setRecalageCmds(cmdsItem);
      // ★ Une colonne par date de report proposée dans « Historique & projection »
      const datesReport = datesReportProposees(cmdsItem);
      if (mode === 'recalage' && datesReport.length > 0) {
        setHistogramData(prev => datesReport.reduce((acc, d) => insererDateReport(acc, d), prev));
      }
    } catch (err) {
      console.error('Erreur chargement recalage_cmde_frn', err);
      setRecalageCmds([]);
    }
  };
  const filterDataByDepartment = (department) => {
    const filtered = data.filter(row => row.Departement === department);
    setFilteredData(filtered);
  };
  const COLUMN_RENAMES = {
    commande_a_10_jours_frn: {
      "Id": null,
      "Numero de commande": "Numéro de commande",
      "Numero de ligne": "N° de ligne",
      "Description": "Déscription"
    },
    recalage_cmde_frn: {
      "Numero de commande": "Numéro de commande",
      "Numero de ligne": "N° de ligne",
      "Description": "Déscription"
    },
    commandes_frn_sans_ar: {
      "Numero de commande": "Numéro de commande",
      "Description": "Déscription"
    },
    analyse_delai_approvisionnement: {
      "Id": null,
      "Description": "Déscription",
      "Qté liv 10 dernière commande": "Qté 10 dernières livraisons"
    }
  };
  const formatColumn = (tableKey, colName) => {
    const mapping = COLUMN_RENAMES[tableKey];
    if (!mapping) return colName;
    if (mapping[colName] === null) return null;
    return mapping[colName] || colName;
  };
  // ================= GRAPHE STOCK PROJETÉ =================
  // Courbe "Stock projeté" — toujours la valeur réelle / actuelle, indépendante de
  // toute simulation (ni saisie manuelle, ni recalage proposé), pour rester une
  // référence stable pendant que l'utilisateur teste des scénarios.
  const baseChartData = useMemo(() => {
    if (!histogramData || histogramData.length === 0) return null;
    return buildChartData(histogramData, granularity);
  }, [histogramData, granularity]);
  // Lignes "historique" recalculées avec la simulation manuelle (Simu cmde Frn) déjà
  // appliquée — alimente le tableau et la courbe "votre simulation" du graphe.
  const manuallyAdjustedRows = useMemo(() => {
    if (!histogramData || histogramData.length === 0) return histogramData;
    if (!projectedStockValues || projectedStockValues.length === 0) return histogramData;
    return histogramData.map((row, idx) => ({
      ...row,
      'stock projete': projectedStockValues[idx] !== undefined ? projectedStockValues[idx] : row['stock projete'],
    }));
  }, [histogramData, projectedStockValues]);
  // ★ DÉCISIONS OPÉRATEUR — commandes recalées cochées (prises en compte) ou décochées (exclues)
  const cmdsRetenues = useMemo(
    () => recalageCmds.filter((_, i) => !cmdsExclues.has(i)),
    [recalageCmds, cmdsExclues]
  );
  const basculerCmd = (i) => setCmdsExclues(prev => {
    const s = new Set(prev);
    if (s.has(i)) s.delete(i); else s.add(i);
    return s;
  });
  const basculerToutesCmds = () => setCmdsExclues(prev =>
    prev.size === 0 ? new Set(recalageCmds.map((_, i) => i)) : new Set()
  );
  // Courbe "après reports/annulations" — simulation automatique des commandes recalées
  // COCHÉES pour cet item, indépendante de la simulation manuelle ci-dessous.
  const simulatedRows = useMemo(() => {
    if (!histogramData || histogramData.length === 0 || cmdsRetenues.length === 0) return null;
    return buildSimulatedRows(histogramData, cmdsRetenues);
  }, [histogramData, cmdsRetenues]);
  // Graphe final : courbe réelle + (si recalage proposé) courbe verte + (si l'utilisateur
  // a saisi une quantité dans "Simu cmde Frn") courbe violette de sa propre simulation.
  const activeChartData = useMemo(() => {
    if (!baseChartData) return null;
    const baseAgg = aggregateRows(histogramData, granularity);
    const datasets = [...baseChartData.datasets];

    if (simulatedRows) {
      const simuAgg = aggregateRows(simulatedRows, granularity);
      const simuMap = {};
      simuAgg.forEach(r => { simuMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
      const simuDataAligned = baseAgg.map(r => {
        const stockBase = parseFloat(r['stock projete']) || 0;
        const stockSimu = simuMap[r._periodKey] ?? stockBase;
        return Math.round(stockSimu);
      });
      datasets.push({
        label: 'Stock projeté (après reports/annulations)',
        type: 'line',
        data: simuDataAligned,
        borderColor: T2_THEME.ok.fg,
        backgroundColor: 'rgba(30,142,90,0.12)',
        borderWidth: 2.5,
        borderDash: [6, 3],
        fill: false,
        tension: 0.3,
        datalabels: { display: false },
      });
    }

    if (projectedStockValues && projectedStockValues.length > 0) {
      const manualAgg = aggregateRows(manuallyAdjustedRows, granularity);
      const manualMap = {};
      manualAgg.forEach(r => { manualMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
      const manualDataAligned = baseAgg.map(r => {
        const stockBase = parseFloat(r['stock projete']) || 0;
        const stockManual = manualMap[r._periodKey] ?? stockBase;
        return Math.round(stockManual);
      });
      datasets.push({
        label: 'Stock projeté (votre simulation)',
        type: 'line',
        data: manualDataAligned,
        borderColor: '#7C3AED',
        backgroundColor: 'rgba(124,58,237,0.12)',
        borderWidth: 2.5,
        borderDash: [2, 2],
        fill: false,
        tension: 0.3,
        datalabels: { display: false },
      });
    }

    return { ...baseChartData, datasets };
  }, [baseChartData, histogramData, granularity, simulatedRows, manuallyAdjustedRows, projectedStockValues]);
  // ================= EXPORT EXCEL — ajouts simulés + reports / annulations =================
  // Feuille 1 « Ajouts simulés »       : quantités saisies dans « Simu cmde Frn » (date, qté, stock avant / après)
  // Feuille 2 « Reports - annulations » : commandes fournisseur recalées pour cet article
  // Feuille 3 « Projection »            : toutes les dates, stock projeté initial et simulé
  const exporterSimulation = async () => {
    if (!histogramData || histogramData.length === 0) return;
    setExportSimu(true);
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'MYSSA';
      wb.created = new Date();
      const article = filteredData[0] || {};
      const item = article.Item || histogramData[0]?.Item || '';
      const desc = article.Description || histogramData[0]?.description || '';
      const NAVY = 'FF0D2140';
      const EUR_QTY = '#,##0;[Red]-#,##0';
      const feuille = (nom, titre, colonnes) => {
        const ws = wb.addWorksheet(nom, { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
        ws.columns = colonnes.map(([, w]) => ({ width: w }));
        ws.getCell('A1').value = titre;
        ws.getCell('A1').font = { bold: true, size: 14, color: { argb: NAVY } };
        ws.getCell('A2').value = `${item} — ${desc} · export du ${new Date().toLocaleString('fr-FR')}`;
        ws.getCell('A2').font = { italic: true, color: { argb: 'FF64748B' } };
        const h = ws.getRow(4);
        h.values = colonnes.map(([t]) => t);
        h.eachCell(c => {
          c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
          c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        });
        h.height = 30;
        return ws;
      };
      const centrer = (row) => row.eachCell(c => { c.alignment = { horizontal: 'center', vertical: 'middle' }; });
      const stockInitial = (i) => Math.round(parseFloat(histogramData[i]['stock projete']) || 0);
      const stockSimule = (i) => Math.round(projectedStockValues[i] !== undefined ? projectedStockValues[i] : parseFloat(histogramData[i]['stock projete']) || 0);
      const typeLib = (r) => (
        r._prochaineLivraison ? 'Prochaine livraison possible'
        : r._dateReport ? 'Date de report proposée'
        : r._ajoutUtilisateur ? 'Date ajoutée (simulation)'
        : (r['type de donnee'] || '')
      );

      // 0) ★ RÉSUMÉ DES ACTIONS À SAISIR DANS L'ERP (1er onglet)
      //    - recalages proposés COCHÉS par l'opérateur (avec n° de commande / ligne)
      //    - simulations saisies dans « Simu cmde Frn » (sur une date de report : commandes concernées ;
      //      sinon : nouvelle commande fournisseur à passer)
      const actions = [];
      recalageCmds.forEach((c, idxCmd) => {
        if (cmdsExclues.has(idxCmd)) return;
        const annul = (c.statut || '').trim().toUpperCase() === 'ANNULATION'
          || c['Report proposé'] === 'ANNULATION'
          || (lireDateLocale(c['Report proposé'])?.getFullYear() ?? 0) >= ANNEE_ANNULATION;
        const lib = getStockprojeteStatutLabel(c).label;
        if (!annul && lib === 'Inchangé') return;
        actions.push({
          action: annul ? 'Annuler la ligne de commande' : lib.startsWith('Avancement') ? 'Avancer la livraison' : 'Reporter la livraison',
          fournisseur: c.Fournisseur || '',
          commande: c['Numero de commande'] ?? '',
          ligne: c['Numero de ligne'] ?? '',
          qte: parseFloat(c['Quantité commandée']) || 0,
          dateActuelle: formatDate(c['Date de livraison']),
          nouvelleDate: annul ? 'ANNULATION' : formatDate(c['Report proposé']),
          origine: 'Proposition validée',
        });
      });
      histogramData.forEach((row, i) => {
        const q = parseFloat(simuCmdeFrnValues[i]) || 0;
        if (!q) return;
        const dRow = lireDateLocale(row.date) || parseLocalDate(row.date);
        // Commandes dont le report proposé tombe à cette date
        const cmdsDate = row._dateReport && dRow
          ? recalageCmds.filter(c => { const d = lireDateLocale(c['Report proposé']); return d && d.toDateString() === dRow.toDateString(); })
          : [];
        if (cmdsDate.length > 0) {
          actions.push({
            action: 'Recaler à cette date (qté simulée)',
            fournisseur: [...new Set(cmdsDate.map(c => c.Fournisseur).filter(Boolean))].join(' / '),
            commande: cmdsDate.map(c => c['Numero de commande']).filter(v => v != null).join(' / '),
            ligne: cmdsDate.map(c => c['Numero de ligne']).filter(v => v != null).join(' / '),
            qte: q,
            dateActuelle: cmdsDate.map(c => formatDate(c['Date de livraison'])).join(' / '),
            nouvelleDate: formatDate(row.date),
            origine: 'Simulation opérateur',
          });
        } else {
          actions.push({
            action: 'Passer une nouvelle commande fournisseur',
            fournisseur: article.Fournisseur || '',
            commande: '', ligne: '',
            qte: q,
            dateActuelle: '',
            nouvelleDate: formatDate(row.date),
            origine: 'Simulation opérateur',
          });
        }
      });
      const ws0 = feuille('Résumé actions ERP', `Actions à saisir dans l'ERP — ${modeModale === 'recalage' ? 'commandes à recaler' : 'appro à lancer'}`, [
        ['N°', 6], ['Action', 34], ['Item', 18], ['Fournisseur', 24], ['N° commande', 16], ['N° ligne', 10],
        ['Qté', 10], ['Date de livraison actuelle', 18], ['Nouvelle date', 16], ['Origine', 20], ['Fait dans l\'ERP', 14],
      ]);
      actions.forEach((a, k) => {
        const ligne = ws0.getRow(5 + k);
        ligne.values = [k + 1, a.action, item, a.fournisseur, a.commande, a.ligne, a.qte, a.dateActuelle, a.nouvelleDate, a.origine, ''];
        ligne.getCell(7).numFmt = EUR_QTY;
        centrer(ligne);
        ligne.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
        const couleur = a.action.startsWith('Annuler') ? 'FFC0392B' : a.action.startsWith('Passer') ? 'FF1A5FA8' : 'FFB7770D';
        ligne.getCell(2).font = { bold: true, color: { argb: couleur } };
        ligne.getCell(11).border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });
      if (actions.length === 0) {
        ws0.getCell('A5').value = 'Aucune action : aucun recalage coché et aucune quantité saisie dans « Simu cmde Frn ».';
      } else {
        ws0.getCell(`A${6 + actions.length}`).value =
          `${actions.length} action(s) — ${actions.filter(a => a.origine === 'Proposition validée').length} proposition(s) validée(s), ${actions.filter(a => a.origine === 'Simulation opérateur').length} simulation(s)`;
        ws0.getCell(`A${6 + actions.length}`).font = { bold: true };
      }

      // 1) Ajouts simulés
      const ws1 = feuille('Ajouts simulés', 'Ajouts simulés (Simu cmde Frn)', [
        ['Item', 18], ['Description', 36], ['Date de réception simulée', 18], ['Type de date', 26],
        ['Qté ajoutée', 13], ['Stock projeté initial', 16], ['Stock projeté simulé', 16],
      ]);
      let r1 = 5;
      histogramData.forEach((row, i) => {
        const q = parseFloat(simuCmdeFrnValues[i]) || 0;
        if (!q) return;
        const ligne = ws1.getRow(r1++);
        ligne.values = [item, desc, formatDate(row.date), typeLib(row), q, stockInitial(i), stockSimule(i)];
        [5, 6, 7].forEach(ci => { ligne.getCell(ci).numFmt = EUR_QTY; });
        centrer(ligne);
      });
      if (r1 === 5) ws1.getCell('A5').value = 'Aucune quantité saisie dans « Simu cmde Frn ».';
      else {
        const tot = ws1.getRow(r1);
        tot.getCell(4).value = 'Total ajouté';
        tot.getCell(5).value = { formula: `SUM(E5:E${r1 - 1})` };
        tot.getCell(4).font = { bold: true }; tot.getCell(5).font = { bold: true };
        centrer(tot);
      }

      // 2) Reports / annulations + décision de l'opérateur (case cochée = retenu)
      const ws2 = feuille('Décisions recalage', 'Recalages proposés et décisions de l\'opérateur', [
        ['Décision opérateur', 18], ['Fournisseur', 26], ['N° commande', 15], ['N° ligne', 10], ['Date de livraison', 15], ['Qté', 10],
        ['Report proposé', 15], ['Action', 20], ['Écart (jours)', 12], ['Approvisionneur', 18],
      ]);
      let r2 = 5;
      recalageCmds.forEach((c, idxCmd) => {
        const retenu = !cmdsExclues.has(idxCmd);
        const annul = (c.statut || '').trim().toUpperCase() === 'ANNULATION' || c['Report proposé'] === 'ANNULATION';
        const o = parseLocalDate(c['Date de livraison']) || new Date(c['Date de livraison']);
        const n = parseLocalDate(c['Report proposé']) || new Date(c['Report proposé']);
        const ecart = !annul && o && n && !isNaN(o) && !isNaN(n) ? Math.round((n - o) / 86400000) : null;
        const ligne = ws2.getRow(r2++);
        ligne.values = [
          retenu ? 'Retenu' : 'Exclu',
          c.Fournisseur || '', c['Numero de commande'] ?? '', c['Numero de ligne'] ?? '',
          formatDate(c['Date de livraison']), parseFloat(c['Quantité commandée']) || 0,
          annul ? 'ANNULATION' : formatDate(c['Report proposé']), getStockprojeteStatutLabel(c).label,
          ecart, c.Approvisionneur || '',
        ];
        ligne.getCell(6).numFmt = EUR_QTY;
        centrer(ligne);
        ligne.getCell(1).font = { bold: true, color: { argb: retenu ? 'FF1E8E5A' : 'FFC0392B' } };
        ligne.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: retenu ? 'FFEAF8F1' : 'FFFCEDEB' } };
        if (!retenu) {
          for (let ci = 2; ci <= 10; ci++) ligne.getCell(ci).font = { strike: true, color: { argb: 'FF94A3B8' } };
        }
      });
      if (r2 > 5) {
        const tot = ws2.getRow(r2 + 1);
        tot.getCell(1).value = `${cmdsRetenues.length} retenue(s) / ${recalageCmds.length}`;
        tot.getCell(1).font = { bold: true };
      }
      if (r2 === 5) ws2.getCell('A5').value = 'Aucun report ni annulation proposé pour cet article.';

      // 3) Projection complète
      const ws3 = feuille('Projection', 'Projection du stock — initiale et simulée', [
        ['Date', 14], ['Type de donnée', 26], ['Livraison fournisseur', 14], ['Départ client', 14],
        ['Simu cmde Frn', 14], ['Stock projeté initial', 16], ['Stock projeté simulé', 16],
      ]);
      histogramData.forEach((row, i) => {
        const ligne = ws3.getRow(5 + i);
        ligne.values = [
          formatDate(row.date), typeLib(row),
          Math.round(parseFloat(row['livraison fournisseur']) || 0), Math.round(parseFloat(row['depart client']) || 0),
          parseFloat(simuCmdeFrnValues[i]) || null, stockInitial(i), stockSimule(i),
        ];
        [3, 4, 5, 6, 7].forEach(ci => { ligne.getCell(ci).numFmt = EUR_QTY; });
        centrer(ligne);
        if (row._prochaineLivraison) {
          ligne.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F5FB' } }; });
        } else if (row._dateReport) {
          ligne.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF6E0' } }; });
        } else if (row._ajoutUtilisateur) {
          ligne.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3EEFF' } }; });
        }
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MYSSA_${modeModale === 'recalage' ? 'recalage' : 'appro_a_lancer'}_${String(item).replace(/[^\w.-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert(`Erreur lors de l'export Excel : ${e.message}`);
    } finally {
      setExportSimu(false);
    }
  };
  // ================= STATUT FIN DE PÉRIODE (bandeau modal) =================
  // Dernière valeur de "stock projeté" :
  //   1) simulation manuelle (Simu cmde Frn) si l'utilisateur a saisi quelque chose
  //   2) sinon simulation automatique du recalage déjà proposé, si elle existe
  //   3) sinon projection brute
  //   - positive  -> rupture temporaire : un recalage de commande fournisseur suffit
  //   - négative/nulle -> il faut passer une nouvelle commande fournisseur
  const getProjectedStockStatus = () => {
    if (!histogramData || histogramData.length === 0) return null;
    const lastIndex = histogramData.length - 1;
    const rawLast = parseFloat(histogramData[lastIndex]['stock projete']);
    let lastValue;
    if (projectedStockValues[lastIndex] !== undefined) {
      lastValue = parseFloat(projectedStockValues[lastIndex]);
    } else if (simulatedRows && simulatedRows.length > 0) {
      lastValue = parseFloat(simulatedRows[simulatedRows.length - 1]['stock projete']);
    } else {
      lastValue = rawLast;
    }
    if (isNaN(lastValue)) return null;
    if (lastValue > 0) {
      return {
        level: 'warning',
        label: 'Rupture temporaire',
        message: "Le stock projeté redevient positif en fin de période : un recalage de commande fournisseur suffit, pas besoin de passer une nouvelle commande.",
      };
    }
    return {
      level: 'danger',
      label: 'Nouvelle commande nécessaire',
      message: "Le stock projeté reste négatif (ou nul) en fin de période : une nouvelle commande fournisseur est nécessaire.",
    };
  };
  const projectedStockStatus = getProjectedStockStatus();
  // ================= TABLEAUX — filtres par colonne =================
  const setFiltre = (col, valeur) => setFiltres(prev => ({ ...prev, [col]: valeur }));
  const filtresActifs = Object.entries(filtres).filter(([, v]) => v && v.trim() !== '');

  const appliquerFiltres = (rows) => {
    if (!filtresActifs.length) return rows;
    return rows.filter(row =>
      filtresActifs.every(([col, q]) => {
        const brut = row[col];
        if (brut == null) return false;
        const cherche = q.trim().toLowerCase();
        const texte = String(brut).toLowerCase();
        const affiche = String(formatDate(brut)).toLowerCase();
        return texte.includes(cherche) || (/date|report/i.test(col) && affiche.includes(cherche));
      })
    );
  };

  const filtreVisible = (table, col, label) => {
    const regle = SANS_FILTRE_AP[table];
    if (regle === '*') return false;
    const n = normaliser(col);
    const l = normaliser(label);
    if (Array.isArray(regle) && (regle.includes(n) || regle.includes(l))) return false;
    if (MOTS_SANS_FILTRE.some(mot => n.includes(mot) || l.includes(mot))) return false;
    return true;
  };

  // ★ En-têtes centrés au milieu de la colonne (style inline : prioritaire sur Approvisionnement.css,
  //   y compris sur la classe "num" qui aligne les colonnes numériques à droite)
  const STYLE_TH       = { textAlign: 'center', verticalAlign: 'middle' };
  const STYLE_TH_LABEL = { textAlign: 'center', justifyContent: 'center', width: '100%' };
  const STYLE_FILTRE   = { textAlign: 'center' };

  const renderTh = (table, col, label, extraClass = '') => {
    if (!filtreVisible(table, col, label)) {
      return (
        <th key={col} className={`${extraClass} ap-th-nofilter`} style={STYLE_TH}>
          <div className="ap-th-label" style={STYLE_TH_LABEL}>{label}</div>
        </th>
      );
    }
    return (
      <th key={col} className={extraClass} style={STYLE_TH}>
        <div className="ap-th-label" style={STYLE_TH_LABEL}>{label}</div>
        <input
          type="text"
          style={STYLE_FILTRE}
          className={`ap-filter ${filtres[col] ? 'active' : ''}`}
          value={filtres[col] || ''}
          onChange={(e) => setFiltre(col, e.target.value)}
          placeholder="Filtrer…"
          aria-label={`Filtrer la colonne ${label}`}
        />
      </th>
    );
  };

  // ================= TÂCHES — mise à jour statut / commentaire =================
  const majTache = (numeroTache, champ, valeur) => {
    setData(prev => prev.map(r => (r['Numero tache'] === numeroTache ? { ...r, [champ]: valeur } : r)));
  };

  const sauverTache = async (numeroTache, commentaire, statut) => {
    try {
      await fetch(`${API}/taches/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_tache: numeroTache, commentaire, statut }),
      });
    } catch (err) { console.error('Erreur sauvegarde PG :', err); }
    if (String(numeroTache || '').startsWith('A')) {
      try {
        await fetch(`${API}/base_commentaire_animation/update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ numero_tache: numeroTache, commentaire }),
        });
      } catch (err) { console.error('Erreur base_commentaire_animation :', err); }
    }
  };

  // ================= CONFIGURATION DE CHAQUE TABLEAU =================
  const cellDefaut = (col, value) => {
    if (value != null && /date|report/i.test(col)) return <td key={col} className="ap-mono">{formatDate(value)}</td>;
    if (['Item'].includes(col)) return <td key={col} className="ap-mono ap-strong">{value}</td>;
    if (['Numero de commande', 'Numero de ligne', 'Numero tache'].includes(col)) return <td key={col} className="ap-mono">{value}</td>;
    if (col === 'Description') return <td key={col} className="ap-td-wrap">{value}</td>;
    if (estNombre(value)) return <td key={col} className="num ap-mono">{Number(String(value).replace(',', '.')).toLocaleString('fr-FR')}</td>;
    return <td key={col}>{value}</td>;
  };

  const configTableau = (() => {
    if (!activeTable || !CARTES_AP.some(c => c.table === activeTable)) return null;
    if (!data.length) {
      return { rows: [], columns: [] };
    }
    switch (activeTable) {
      // ── Card15 : commandes fournisseurs à 10 jours
      case 'table1': {
        const rows = [...data].sort((a, b) =>
          new Date(a['Date de livraison prévue']) - new Date(b['Date de livraison prévue'])
        );
        return {
          rows,
          columns: Object.keys(data[0]).filter(c => c !== 'Id'),
          renderCell: (row, col) => {
            const value = row[col];
            if (col === 'Horizon') return <td key={col}><Pill tone={toneHorizon(value)}>{value}</Pill></td>;
            return cellDefaut(col, value);
          },
        };
      }
      // ── Card16 : recalage stock fort (clic sur l'Item = fiche article)
      case 'table2':
        return {
          rows: data,
          columns: Object.keys(data[0] || {}),
          rowClass: (row) => `ap-row-${toneHorizon(row['Date'])}`,
          // ★ Toute la ligne est cliquable → ouvre la modale « Appro à lancer »
          onRowClick: (row) => { if (row.Item) handleItemClick(row.Item, 'appro'); },
          renderCell: (row, col) => {
            const value = row[col];
            if (col === 'Item') {
              // Item gardé en style lien (repère visuel) — le clic est géré par la ligne
              return (
                <td key={col}>
                  <span className="ap-link">{value}</span>
                </td>
              );
            }
            if (col === 'Date') return <td key={col}><Pill tone={toneHorizon(value)}>{value}</Pill></td>;
            return cellDefaut(col, value);
          },
        };
      // ── Card18 : recalage des commandes fournisseurs (une ligne par Item — clic sur la ligne = fiche article)
      case 'table3': {
        const lignesRegroupees = regrouperParItem(data, formatDate);
        return {
          rows: lignesRegroupees,
          columns: [
            ...Object.keys(data[0]).filter(c => c !== 'ID' && c !== 'Id'),
            'Nb lignes',
          ],
          rowClass: (row) => {
            const lignes = row._lignes || [row];
            // Rouge seulement si TOUTES les lignes regroupées sont des annulations
            const annulation = lignes.every(l => String(l['statut'] ?? '').trim().toUpperCase() === 'ANNULATION');
            const inf2 = lignes.some(l => Object.values(l).some(v => String(v ?? '').trim().toLowerCase() === 'inf +2 semaines'));
            return annulation ? 'ap-row-danger' : inf2 ? 'ap-row-warn' : '';
          },
          // ★ Ligne cliquable → ouvre la fiche article sans changer le tableau affiché
          onRowClick: (row) => { if (row.Item) handleItemClick(row.Item, 'recalage'); },
          renderCell: (row, col) => {
            const value = row[col];
            // ★ Valeurs regroupées : une valeur par ligne, dans la même cellule
            if (typeof value === 'string' && value.includes(SEP_REGROUPE)) {
              return (
                <td key={col} className="ap-td-multi">
                  {value.split(SEP_REGROUPE).map((v, k) => {
                    const t = v.trim();
                    let contenu = t;
                    if (t.toUpperCase() === 'ANNULATION') contenu = <Pill tone="danger">Annulation</Pill>;
                    else if (t.toLowerCase() === 'inf +2 semaines') contenu = <Pill tone="warn">{t}</Pill>;
                    return <div key={k} className="ap-multi-line">{contenu}</div>;
                  })}
                </td>
              );
            }
            if (String(value ?? '').trim().toUpperCase() === 'ANNULATION') return <td key={col}><Pill tone="danger">Annulation</Pill></td>;
            if (String(value ?? '').trim().toLowerCase() === 'inf +2 semaines') return <td key={col}><Pill tone="warn">{value}</Pill></td>;
            return cellDefaut(col, value);
          },
        };
      }
      // ── Card19 : commandes fournisseurs sans AR
      case 'table4':
        return {
          rows: data,
          columns: Object.keys(data[0]).filter(c => c !== 'ID' && c !== 'Client_id'),
          renderCell: (row, col) => {
            const value = row[col];
            if (col === 'Statut AR') {
              // ★ AR « Manquant » : texte rouge pastel sur fond rose très clair
              if (value === 'manquant') {
                return (
                  <td key={col}>
                    <span className="ap-badge" style={{ background: '#FDECEC', color: '#D9534F', border: '1px solid #F5C6C5', fontWeight: 600 }}>{value}</span>
                  </td>
                );
              }
              return <td key={col}><Pill tone="ok">{value}</Pill></td>;
            }
            return cellDefaut(col, value);
          },
        };
      // ── Card20 : analyse des délais d'approvisionnement
      case 'table20': {
        const columns = Object.keys(data[0]);
        return {
          rows: data.filter(row => row['Délais proposé'] !== 'pas de changement proposé'),
          columns,
          widths: columns.map(c => COLUMN_WIDTHS_TABLE20[c] || TABLE20_DEFAULT_WIDTH),
          renderCell: (row, col) => {
            const value = row[col];
            if ((col === 'Ecart' || col === 'Evolution') && estNombre(value)) {
              const n = parseFloat(value);
              return <td key={col} className="num"><Pill tone={n < 0 ? 'ok' : n > 0 ? 'danger' : 'neutral'}>{n > 0 ? `+${n}` : n}</Pill></td>;
            }
            return cellDefaut(col, value);
          },
        };
      }
      // ── Card21 : tâches APPRO
      case 'table21':
        return {
          rows: data,
          columns: TABLE21_COLUMNS,
          className: 'ap-table-taches',
          renderCell: (row, col) => {
            const value = row[col];
            if (col === 'Date') return <td key={col} className="ap-mono">{formatDate(value)}</td>;
            if (col === 'Commentaires') {
              return (
                <td key={col} className="ap-td-comment">
                  <AutoTextarea
                    className="ap-textarea"
                    value={value || ''}
                    placeholder="Ajouter un commentaire"
                    aria-label={`Commentaire de la tâche ${row['Numero tache'] || ''}`}
                    onChange={(e) => majTache(row['Numero tache'], 'Commentaires', e.target.value)}
                    onBlur={(e) => sauverTache(row['Numero tache'], e.target.value || '', row['Statut'] || '')}
                  />
                </td>
              );
            }
            if (col === 'Statut') {
              const classe =
                value === 'ouvert' ? 'ap-statut-ouvert' :
                value === 'en cours' ? 'ap-statut-encours' :
                value === 'soldé' ? 'ap-statut-solde' : '';
              return (
                <td key={col}>
                  <select
                    className={`ap-select ${classe}`}
                    value={value || 'ouvert'}
                    aria-label={`Statut de la tâche ${row['Numero tache'] || ''}`}
                    onChange={(e) => {
                      const nouveau = e.target.value;
                      majTache(row['Numero tache'], 'Statut', nouveau);
                      sauverTache(row['Numero tache'], row['Commentaires'] || '', nouveau);
                    }}
                  >
                    <option value="ouvert">ouvert</option>
                    <option value="en cours">en cours</option>
                    <option value="soldé">soldé</option>
                  </select>
                </td>
              );
            }
            if (col === 'Description') {
              const champs = decouperDescription(value);
              if (champs) {
                return (
                  <td key={col} className="ap-td-desc">
                    <div className="ap-desc-grid">
                      {champs.map((c, idx) => {
                        const vide = c.valeur === '—' || /^-+$/.test(c.valeur) || /pas de/i.test(c.valeur);
                        return (
                          <div key={idx} className={`ap-desc-field ${c.label === 'Commentaire' ? 'ap-desc-comment' : ''} ${vide ? 'ap-desc-empty' : ''}`}>
                            <span className="ap-desc-label">{c.label}</span>
                            <span className="ap-desc-value">{/^-+$/.test(c.valeur) ? '—' : c.valeur}</span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                );
              }
              return <td key={col} className="ap-td-wrap">{value && value.includes('Commentaire :') ? renderCommentaire(value) : value}</td>;
            }
            if (col === 'Numero tache') return <td key={col} className="ap-mono">{value}</td>;
            return <td key={col}>{value}</td>;
          },
        };
      default:
        return null;
    }
  })();

  const carteActive = CARTES_AP.find(c => c.table === activeTable);
  const lignesVisibles = configTableau ? appliquerFiltres(configTableau.rows || []) : [];

  const retourGraphiques = () => {
    setActiveTable(null);
    setShowCharts(true);
    setFiltres({});
  };
  // ================= RENDER =================
  return (
    <div className="ap-page">
      {/* ★ Données centrées dans les tableaux (prioritaire sur Approvisionnement.css, y compris la classe "num").
            Exceptions lisibilité : bloc multi-champs de la description des tâches et libellés de la fenêtre article. */}
      <style>{`
        .ap-table td, .ap-table td.num { text-align: center !important; vertical-align: middle; }
        .ap-table td.ap-td-desc { text-align: left !important; }
        .myssa-modal-table-card th,
        .myssa-modal-table-card td:not(.myssa-modal-row-label) { text-align: center !important; }
        .ap-table tr.ap-row-clickable { cursor: pointer; }
        .ap-table td.ap-td-multi .ap-multi-line { white-space: nowrap; line-height: 1.5; }
        .ap-table td.ap-td-multi .ap-multi-line + .ap-multi-line { border-top: 1px dashed #E3E9F0; padding-top: 2px; margin-top: 2px; }
        .ap-table tr.ap-row-clickable:hover td { background: #EEF5FC; }
      `}</style>
      <div className="ap-container">

        {/* ── Header ── */}
        <div className="ap-header">
          <div className="ap-header-left">
            <div className="ap-title-row">
              <div className="ap-accent" />
              <h1 className="ap-title">Approvisionnement</h1>
              <span className="ap-chip-dept">APPRO</span>
            </div>
         
          </div>
        </div>

        {/* ── Cartes ── */}
       <div
  className="ap-cards"
  role="group"
  aria-label="Choisir un tableau"
  style={{ marginTop: '40px', paddingLeft: '80px' }}
>
          {CARTES_AP.map(({ table, source, titre, Component }) => {
            const actif = activeTable === table;
            return (
              <button
                key={table}
                type="button"
                className={`ap-card-btn ${actif ? 'active' : ''}`}
                aria-pressed={actif}
                aria-label={titre}
                onClick={() => { setFiltres({}); loadTableData(source, table); }}
              >
                <Component />
              </button>
            );
          })}
        </div>

        {/* ── Graphiques (vue par défaut) ── */}
        {showCharts && (
          <div className="ap-graphs">
            <div className="ap-card ap-graph-card ap-graph-pie"><Camembert3 /></div>
            <div className="ap-card ap-graph-card ap-graph-bar"><Graph2 /></div>
          </div>
        )}

        {/* ── Tableau ── */}
        {configTableau && (
          <div className="ap-card ap-table-card">
            <div className="ap-table-head">
              <div className="ap-table-head-left">
                <div className="ap-table-title">{carteActive?.titre || 'Détail'}</div>
                <span className="ap-count">
                  {lignesVisibles.length}{filtresActifs.length ? ` / ${configTableau.rows.length}` : ''} ligne{lignesVisibles.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="ap-table-head-right">
                {filtresActifs.length > 0 && (
                  <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setFiltres({})}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    Effacer les filtres ({filtresActifs.length})
                  </button>
                )}
                {activeTable === 'table2' && (
                  <button type="button" className="ap-btn ap-btn-ghost" onClick={closeDetailModal}>
                    Réinitialiser
                  </button>
                )}
                <button type="button" className="ap-btn ap-btn-ghost" onClick={retourGraphiques}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Graphiques
                </button>
              </div>
            </div>

            <div className="ap-table-scroll">
              {configTableau.columns.length === 0 ? (
                <div className="ap-empty">Aucune donnée à afficher.</div>
              ) : (
                <>
                  <table
                    className={`ap-table ${configTableau.className || ''}`}
                    style={configTableau.widths ? { tableLayout: 'fixed', minWidth: '100%' } : {}}
                  >
                    {configTableau.widths && (
                      <colgroup>
                        {configTableau.widths.map((w, i) => <col key={i} style={{ width: w }} />)}
                      </colgroup>
                    )}
                    <thead>
                      <tr>
                        {configTableau.columns.map(col =>
                          renderTh(activeTable, col, LIBELLES_AP[col] || col, estNombre(configTableau.rows[0]?.[col]) ? 'num' : '')
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {lignesVisibles.map((row, i) => (
                        <tr
                          key={row['Numero tache'] || i}
                          className={`${configTableau.rowClass ? configTableau.rowClass(row) : ''}${configTableau.onRowClick ? ' ap-row-clickable' : ''}`}
                          onClick={configTableau.onRowClick ? () => configTableau.onRowClick(row) : undefined}
                          title={configTableau.onRowClick ? 'Ouvrir la fiche article' : undefined}
                        >
                          {configTableau.columns.map(col => configTableau.renderCell(row, col))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {lignesVisibles.length === 0 && (
                    <div className="ap-empty">
                      {configTableau.rows.length === 0 ? 'Aucune donnée à afficher.' : 'Aucune ligne ne correspond aux filtres.'}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {/* ===== MODAL DÉTAIL ITEM (infos / stocks / historique) ===== */}
      {/* Le scroll se fait sur myssa-modal-content lui-même (style inline,
          exactement comme la modale qui fonctionne dans Stockprojete.jsx) :
          tout le contenu — header compris — défile en un seul bloc, le header
          restant épinglé en haut grâce à position: sticky. */}
      {showDetailModal && (
        <div className="myssa-modal-overlay" onClick={closeDetailModal}>
          <div
            className="myssa-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: MODAL_CONFIG.maxWidth,
              maxHeight: `${MODAL_CONFIG.maxHeightVh}vh`,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <div className="myssa-modal-header">
              <div className="myssa-modal-title">
                <span
                  className="myssa-modal-title-bar"
                  style={modeModale === 'recalage' ? { background: 'linear-gradient(180deg, #8A5A00, #E0A526)' } : undefined}
                />
                {modeModale === 'recalage' ? 'Commandes à recaler' : 'Appro à lancer'}
                {filteredData[0]?.Item ? ` — ${filteredData[0].Item}` : ''}
              </div>
              <button className="myssa-modal-close" onClick={closeDetailModal} aria-label="Fermer">
                ×
              </button>
            </div>
            {itemClickError && (
              <div className="myssa-modal-banner myssa-modal-banner-warning">
                <span className="myssa-modal-banner-dot" />
                <span className="myssa-modal-banner-label">Donnée incomplète</span>
                <span className="myssa-modal-banner-sep">·</span>
                <span className="myssa-modal-banner-message">{itemClickError}</span>
              </div>
            )}
            {projectedStockStatus && (
              <div className={`myssa-modal-banner myssa-modal-banner-${projectedStockStatus.level}`}>
                <span className="myssa-modal-banner-dot" />
                <span className="myssa-modal-banner-label">{projectedStockStatus.label}</span>
                <span className="myssa-modal-banner-sep">·</span>
                <span className="myssa-modal-banner-message">{projectedStockStatus.message}</span>
              </div>
            )}
            <div className="myssa-modal-body">
              <div className="myssa-modal-row">
                {/* Tableau 1 : infos item */}
                <div className="myssa-modal-table-card myssa-modal-table-item">
                  <div className="myssa-modal-card-title">Détails article</div>
                  <table>
                    <tbody>
                      {[
                        "Item",
                        "Description",
                        "Délai d'approvisionnement (jours ouvrés)",
                        "Délais réel",
                        "Date de livraison",
                        "Date de rupture"
                      ].map((col, colIndex) => (
                        <tr key={colIndex}>
                          <td className="myssa-modal-row-label">{col}</td>
                          {filteredData.map((row, rowIndex) => {
                            let cellValue = row[col];
                            let cellStyle = {};
                            if (col === "Statut appro") {
                              if (cellValue === "rupture")            cellStyle.backgroundColor = T2_THEME.j.bg;
                              else if (cellValue === "rupture sèche") cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (col === "Date de livraison" && cellValue) {
                              cellStyle.backgroundColor = T2_THEME.ok.bg;
                            }
                            if (col === "Date de rupture" && cellValue) {
                              cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (col === "Délais réel") {
                              cellValue = cellValue != null ? Math.round(Number(cellValue)) : cellValue;
                            }
                            return <td key={rowIndex} style={cellStyle}>{cellValue}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Tableau 2 : stocks */}
                <div className="myssa-modal-table-card myssa-modal-table-stock">
                  <div className="myssa-modal-card-title">Stocks</div>
                  <table>
                    <tbody>
                      {["Stock", "Cmde client", "Cmde fournisseur", "Stock projeté", "Stock mini"].map((col, colIndex) => (
                        <tr key={colIndex}>
                          <td className="myssa-modal-row-label">{col}</td>
                          {filteredData.map((row, rowIndex) => {
                            let cellValue = row[col];
                            let cellStyle = {};
                            if (col === "Stock projeté") {
                              if (cellValue > 0)      cellStyle.backgroundColor = T2_THEME.ok.bg;
                              else if (cellValue < 0) cellStyle.backgroundColor = T2_THEME.retard.bg;
                            }
                            if (["Stock", "Cmde client", "Cmde fournisseur", "Stock projeté", "Stock mini"].includes(col)) {
                              cellValue = cellValue != null && cellValue !== ""
                                ? Math.round(Number(cellValue)) : cellValue;
                            }
                            return <td key={rowIndex} style={cellStyle}>{cellValue}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Tableau historique */}
              {histogramData.length > 0 && (
                <div className="myssa-modal-table-card myssa-modal-table-histogram">
                  <div className="myssa-modal-card-title myssa-modal-card-title-row">
                    <span>Historique &amp; projection</span>
                    {/* ★ Export Excel : ajouts simulés + reports / annulations + projection */}
                    <button
                      type="button"
                      onClick={exporterSimulation}
                      disabled={exportSimu}
                      title="Exporter les ajouts simulés (Simu cmde Frn), les reports / annulations et la projection"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', margin: 0,
                        padding: '5px 12px', borderRadius: 7, border: 'none', whiteSpace: 'nowrap',
                        background: exportSimu ? '#9CA3AF' : '#1D6F42', color: '#FFFFFF',
                        fontSize: 12, fontWeight: 600, cursor: exportSimu ? 'wait' : 'pointer',
                        textTransform: 'none', letterSpacing: 0,
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
                      {exportSimu ? 'Export…' : modeModale === 'recalage' ? 'Excel · décisions recalage' : 'Excel · appro à lancer'}
                    </button>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Type de donnée</th>
                        {histogramData.map((row, index) => {
                          const dateAjout = dateAjoutPossible(histogramData, index);
                          const prec = index > 0 ? dateDeLigne(histogramData[index - 1]) : null;
                          const suiv = index < histogramData.length - 1 ? dateDeLigne(histogramData[index + 1]) : null;
                          return (
                          <th
                            key={index}
                            style={{
                              position: 'relative',
                              ...(row._prochaineLivraison ? { ...STYLE_COL_PROCHAINE, color: '#0B6A8F' }
                                : row._dateReport ? { ...STYLE_COL_REPORT, color: '#8A5A00' }
                                : row._ajoutUtilisateur ? { ...STYLE_COL_AJOUT, color: '#5B21B6' }
                                : {}),
                            }}
                            title={
                              row._prochaineLivraison ? "Prochaine date de livraison possible (aujourd'hui + délai appro) : saisis une quantité dans « Simu cmde Frn » pour simuler cette livraison"
                              : row._dateReport ? "Date de report proposée : saisis dans « Simu cmde Frn » la quantité que tu veux recevoir à cette date"
                              : row._ajoutUtilisateur ? "Date ajoutée : modifie-la si besoin, puis saisis une quantité dans « Simu cmde Frn »"
                              : undefined
                            }
                          >
                            {/* ★ Bouton « + » entre deux colonnes : ajoute une date 5 jours avant celle-ci */}
                            {dateAjout && (
                              <button
                                type="button"
                                className="myssa-add-col-btn"
                                onClick={() => ajouterColonneDate(index)}
                                title={`Ajouter une date de livraison au ${dateFR(dateAjout)}`}
                                aria-label={`Ajouter une date de livraison au ${dateFR(dateAjout)}`}
                              >
                                +
                              </button>
                            )}
                            {row._ajoutUtilisateur ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                                <input
                                  type="date"
                                  className="myssa-date-ajout"
                                  value={dateISO(dateDeLigne(row))}
                                  min={prec ? dateISO(new Date(prec.getTime() + UN_JOUR)) : undefined}
                                  max={suiv ? dateISO(new Date(suiv.getTime() - UN_JOUR)) : undefined}
                                  onChange={(e) => changerDateAjoutee(index, e.target.value)}
                                  aria-label="Date de livraison simulée"
                                />
                                <button
                                  type="button"
                                  className="myssa-del-col-btn"
                                  onClick={() => supprimerColonneDate(index)}
                                  title="Supprimer cette date"
                                  aria-label="Supprimer cette date"
                                >
                                  ×
                                </button>
                              </div>
                            ) : formatDate(row.date)}
                            {row._prochaineLivraison && (
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Prochaine livraison</div>
                            )}
                            {row._dateReport && (
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8A5A00' }}>Report proposé</div>
                            )}
                            {row._ajoutUtilisateur && (
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#5B21B6' }}>Date ajoutée</div>
                            )}
                          </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {["livraison fournisseur", "depart client", "stock projete", "Simu cmde Frn"].map((type, typeIndex) => (
                        <tr key={typeIndex}>
                          <td className="myssa-modal-row-label"><strong>{type}</strong></td>
                          {histogramData.map((row, index) => {
                            if (type === "stock projete") {
                              const projected = projectedStockValues[index] !== undefined
                                ? projectedStockValues[index]
                                : row["stock projete"];
                              const bgColor =
                                projected < 0
                                  ? T2_THEME.retard.bg
                                  : stockMiniValue !== null && projected <= stockMiniValue
                                  ? T2_THEME.j.bg
                                  : T2_THEME.ok.bg;
                              return (
                                <td key={index} style={{ backgroundColor: bgColor, ...(styleColonneMarquee(row) ? { boxShadow: styleColonneMarquee(row).boxShadow, fontWeight: 700 } : {}) }}>
                                  {typeof projected === 'number'
                                    ? Math.round(projected)
                                    : Math.round(Number(projected))}
                                </td>
                              );
                            } else if (type === "Simu cmde Frn") {
                              return (
                                <td key={index} style={styleColonneMarquee(row)}>
                                  <input
                                    type="number"
                                    placeholder={row._prochaineLivraison || row._dateReport || row._ajoutUtilisateur ? 'Qté' : undefined}
                                    value={simuCmdeFrnValues[index] || ''}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      const updatedSimu = { ...simuCmdeFrnValues, [index]: newValue };
                                      setSimuCmdeFrnValues(updatedSimu);
                                      recalculateProjectedStock(updatedSimu, histogramData);
                                    }}
                                    style={{ width: '80px' }}
                                  />
                                </td>
                              );
                            } else {
                              return (
                                <td key={index} style={styleColonneMarquee(row)}>
                                  {row[type] != null ? Math.round(Number(row[type])) : row[type]}
                                </td>
                              );
                            }
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {histogramData.some(r => r._prochaineLivraison) && (
                    <div className="myssa-graph-note">
                      <span className="myssa-graph-note-dot" style={{ background: '#2BAADB' }} />
                      Colonne bleue = prochaine date de livraison possible (aujourd'hui + délai appro). Saisis une quantité dans « Simu cmde Frn » sur cette colonne pour simuler une commande passée aujourd'hui : le stock projeté et la courbe violette se mettent à jour.
                    </div>
                  )}
                  <div className="myssa-graph-note" style={{ color: '#5B21B6' }}>
                    <span className="myssa-graph-note-dot" style={{ background: '#7C3AED' }} />
                    « + » entre deux colonnes = ajouter une date de livraison 5 jours avant la colonne suivante (date modifiable, × pour la retirer). Saisis ensuite une quantité dans « Simu cmde Frn » : stock projeté, courbe et export se mettent à jour.
                  </div>
                  {histogramData.some(r => r._dateReport) && (
                    <div className="myssa-graph-note" style={{ color: '#8A5A00' }}>
                      <span className="myssa-graph-note-dot" style={{ background: '#E0A526' }} />
                      Colonnes orange = dates de report proposées. Saisis dans « Simu cmde Frn » les quantités que tu retiens à ces dates, puis « Excel · décisions recalage » pour les extraire.
                    </div>
                  )}
                </div>
              )}
              {/* Commandes fournisseur — décalage possible (même format que Stockprojete.jsx) */}
              {recalageCmds.length > 0 && (
                <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                    ⟳ Commandes fournisseur — recalages proposés
                  </div>
                  <div style={{ fontSize: 11, color: '#1E40AF', background: '#DBEAFE', borderRadius: 6, padding: '5px 10px', marginBottom: 10 }}>
                    La courbe verte en pointillés représente le stock projeté après application des recalages cochés ({cmdsRetenues.length}/{recalageCmds.length}). Décoche une ligne pour l'exclure de la simulation.
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, textAlign: 'center' }}>
                      <thead>
                        <tr style={{ background: '#DBEAFE' }}>
                          <th style={{ padding: '6px 10px', width: 32 }}>
                            <input
                              type="checkbox"
                              aria-label="Tout cocher ou tout décocher"
                              checked={cmdsExclues.size === 0}
                              ref={el => { if (el) el.indeterminate = cmdsExclues.size > 0 && cmdsExclues.size < recalageCmds.length; }}
                              onChange={basculerToutesCmds}
                              style={{ cursor: 'pointer', width: 15, height: 15 }}
                            />
                          </th>
                          {['Fournisseur', 'N° commande', 'Date livraison', 'Qté', 'Report proposé', 'Statut', 'Approvisionneur'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'center', color: '#1E40AF', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recalageCmds.map((c, i) => {
                          const statutLabel = getStockprojeteStatutLabel(c);
                          const isAnnulation = (c.statut || '').trim().toUpperCase() === 'ANNULATION';
                          const exclue = cmdsExclues.has(i);
                          return (
                            <tr
                              key={i}
                              onClick={() => basculerCmd(i)}
                              title={exclue ? 'Cliquer pour prendre en compte ce recalage' : 'Cliquer pour exclure ce recalage'}
                              style={{
                                borderBottom: '0.5px solid #BFDBFE',
                                background: i % 2 === 0 ? '#F0F7FF' : '#FFFFFF',
                                opacity: exclue ? 0.5 : isAnnulation ? 0.75 : 1,
                                textDecoration: exclue ? 'line-through' : 'none',
                                cursor: 'pointer',
                              }}
                            >
                              <td style={{ padding: '6px 10px' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  aria-label={`Prendre en compte la commande ${c['Numero de commande'] ?? ''}`}
                                  checked={!exclue}
                                  onChange={() => basculerCmd(i)}
                                  style={{ cursor: 'pointer', width: 15, height: 15 }}
                                />
                              </td>
                              <td style={{ padding: '6px 10px', fontWeight: 500, color: '#1E40AF' }}>{c.Fournisseur}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'IBM Plex Mono, monospace', color: '#374151' }}>{c['Numero de commande']}</td>
                              <td style={{ padding: '6px 10px', color: '#374151' }}>{formatDate(c['Date de livraison'])}</td>
                              <td style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'center', color: '#0D2140' }}>{fmtQty(c['Quantité commandée'])}</td>
                              <td style={{ padding: '6px 10px', color: isAnnulation ? '#A32D2D' : '#854F0B', fontWeight: 500 }}>
                                {isAnnulation ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>—</span> : formatDate(c['Report proposé'])}
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: statutLabel.bg, color: statutLabel.color, whiteSpace: 'nowrap' }}>
                                  {statutLabel.label}
                                </span>
                              </td>
                              <td style={{ padding: '6px 10px', color: '#6B7280', fontSize: 11 }}>{c.Approvisionneur}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Graphique stock projeté */}
              {histogramData.length > 0 && activeChartData && (
                <div className="myssa-modal-table-card myssa-modal-table-histogram">
                  <div className="myssa-modal-card-title myssa-modal-card-title-row">
                    <span>Évolution du stock</span>
                    <div className="myssa-granularity-group">
                      {[['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']].map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => setGranularity(k)}
                          className={`myssa-granularity-btn${granularity === k ? ' active' : ''}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="myssa-graph-wrapper">
                    <Line data={activeChartData} options={chartOptions} />
                  </div>
                  {projectedStockValues && projectedStockValues.length > 0 && (
                    <div className="myssa-graph-note">
                      <span className="myssa-graph-note-dot" />
                      La courbe violette en pointillés représente le stock projeté avec vos quantités saisies dans « Simu cmde Frn ».
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <style>{`
            .myssa-modal-overlay {
              position: fixed;
              inset: 0;
              background: rgba(13, 33, 64, 0.55);
              backdrop-filter: blur(2px);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
              padding: 24px;
              animation: myssa-fade-in 0.15s ease;
            }
            @keyframes myssa-fade-in {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            .myssa-modal-content {
              background: #FFFFFF;
              border-radius: 14px;
              width: 100%;
              box-shadow: 0 18px 50px rgba(13, 33, 64, 0.35);
              scrollbar-width: thin;
              scrollbar-color: #C7D2E0 #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar {
              width: 10px;
            }
            .myssa-modal-content::-webkit-scrollbar-track {
              background: #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar-thumb {
              background: #C7D2E0;
              border-radius: 8px;
              border: 2px solid #F6F9FC;
            }
            .myssa-modal-content::-webkit-scrollbar-thumb:hover {
              background: #A8B6CA;
            }
            .myssa-modal-banner {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 10px 20px;
              font-size: 12.5px;
              line-height: 1.4;
              border-bottom: 1px solid ${T2_THEME.border};
            }
            .myssa-modal-banner-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              flex-shrink: 0;
            }
            .myssa-modal-banner-warning {
              background: ${T2_THEME.j.bg};
              color: ${T2_THEME.j.fg};
            }
            .myssa-modal-banner-warning .myssa-modal-banner-dot {
              background: ${T2_THEME.j.fg};
            }
            .myssa-modal-banner-danger {
              background: ${T2_THEME.retard.bg};
              color: ${T2_THEME.retard.fg};
            }
            .myssa-modal-banner-danger .myssa-modal-banner-dot {
              background: ${T2_THEME.retard.fg};
            }
            .myssa-modal-banner-label {
              font-weight: 700;
              text-transform: uppercase;
              font-size: 11px;
              letter-spacing: 0.03em;
              white-space: nowrap;
            }
            .myssa-modal-banner-sep {
              opacity: 0.5;
            }
            .myssa-modal-banner-message {
              flex: 1;
            }
            .myssa-modal-header {
              position: sticky;
              top: 0;
              z-index: 5;
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 14px 20px;
              background: #FFFFFF;
              border-bottom: 1px solid #E3E9F0;
            }
            .myssa-modal-title {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #0D2140;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 13px;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              font-weight: 500;
            }
            .myssa-modal-title-bar {
              width: 3px;
              height: 14px;
              background: linear-gradient(180deg, #0D2140, #2BAADB);
              border-radius: 2px;
            }
            .myssa-modal-close {
              background: #FFFFFF;
              border: 1px solid #E3E9F0;
              color: #0D2140;
              width: 30px;
              height: 30px;
              border-radius: 8px;
              font-size: 18px;
              line-height: 1;
              cursor: pointer;
              transition: background 0.15s ease;
            }
            .myssa-modal-close:hover {
              background: #F6F9FC;
            }
            .myssa-modal-body {
              padding: 20px;
              display: flex;
              flex-direction: column;
              gap: 18px;
            }
            .myssa-modal-row {
              display: flex;
              gap: 18px;
              flex-wrap: wrap;
            }
            .myssa-modal-table-card {
              background: #FFFFFF;
              border: 1px solid #E3E9F0;
              border-radius: 12px;
              box-shadow: 0 2px 10px rgba(13, 33, 64, 0.05);
              overflow: hidden;
            }
            .myssa-modal-table-item  { flex: 1 1 360px; }
            .myssa-modal-table-stock { flex: 1 1 280px; }
            .myssa-modal-table-histogram {
              width: 100%;
              overflow-x: auto;
            }
            .myssa-modal-card-title-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
            }
            .myssa-granularity-group {
              display: flex;
              gap: 6px;
            }
            .myssa-granularity-btn {
              background: #FFFFFF;
              color: #64748B;
              border: 1px solid #E3E9F0;
              padding: 3px 11px;
              border-radius: 999px;
              font-size: 11px;
              font-family: 'IBM Plex Sans', sans-serif;
              font-weight: 500;
              cursor: pointer;
              text-transform: none;
              letter-spacing: normal;
            }
            .myssa-granularity-btn.active {
              background: #0D2140;
              border-color: #0D2140;
              color: #FFFFFF;
            }
            .myssa-graph-wrapper {
              padding: 16px;
              height: 340px;
            }
            .myssa-graph-note {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 16px 14px 16px;
              font-size: 11.5px;
              color: #6D28D9;
              line-height: 1.4;
            }
            .myssa-graph-note-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #7C3AED;
              flex-shrink: 0;
            }
            .myssa-modal-card-title {
              font-family: 'IBM Plex Mono', monospace;
              font-size: 11px;
              font-weight: 500;
              letter-spacing: 0.06em;
              text-transform: uppercase;
              color: #64748B;
              background: #F6F9FC;
              padding: 9px 16px;
              border-bottom: 1px solid #E3E9F0;
            }
            .myssa-modal-table-card table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            .myssa-modal-table-histogram table {
              min-width: max-content;
            }
            .myssa-add-col-btn {
              position: absolute;
              left: -9px;
              top: 50%;
              transform: translateY(-50%);
              z-index: 3;
              width: 18px;
              height: 18px;
              padding: 0;
              margin: 0;
              border-radius: 50%;
              border: 1px solid #C4B5FD;
              background: #FFFFFF;
              color: #7C3AED;
              font-size: 13px;
              font-weight: 700;
              line-height: 16px;
              text-align: center;
              cursor: pointer;
              opacity: 0.55;
              transition: opacity 0.15s ease, background 0.15s ease;
            }
            .myssa-add-col-btn:hover,
            .myssa-add-col-btn:focus-visible {
              opacity: 1;
              background: #7C3AED;
              color: #FFFFFF;
            }
            .myssa-date-ajout {
              font-family: 'IBM Plex Mono', monospace;
              font-size: 11px;
              color: #5B21B6;
              border: 1px solid #C4B5FD;
              border-radius: 5px;
              padding: 1px 4px;
              background: #FFFFFF;
            }
            .myssa-del-col-btn {
              width: 18px;
              height: 18px;
              padding: 0;
              margin: 0;
              border: none;
              border-radius: 4px;
              background: transparent;
              color: #7C3AED;
              font-size: 14px;
              line-height: 1;
              cursor: pointer;
            }
            .myssa-del-col-btn:hover {
              background: #EDE9FE;
            }
            .myssa-modal-table-card th {
              background: #FBFCFE;
              color: #64748B;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 10.5px;
              font-weight: 600;
              letter-spacing: 0.03em;
              text-transform: uppercase;
              text-align: center;
              padding: 8px 14px;
              border-bottom: 1px solid #E3E9F0;
              white-space: nowrap;
            }
            .myssa-modal-table-card td {
              padding: 9px 14px;
              text-align: center;
              color: #16243B;
              border-bottom: 1px solid #F0F4F8;
              white-space: nowrap;
            }
            .myssa-modal-table-card tbody tr:last-child td {
              border-bottom: none;
            }
            .myssa-modal-table-card tbody tr:nth-child(even) {
              background: #FBFCFE;
            }
            .myssa-modal-row-label {
              text-align: left !important;
              font-family: 'IBM Plex Mono', monospace;
              font-size: 11.5px;
              color: #8B95A6;
              white-space: nowrap;
            }
          `}</style>
        </div>
      )}
    </div>
  );
};
export default Approvisionnement;